/**
 * WeaveIn.ai Platform - Public Form Page
 * Renders a form for unauthenticated submission via public token.
 * Accessed via /public/forms/{token} — no login required.
 *
 * Visual design comes from the org's resolved design template (`theme`,
 * delivered by the backend). Field rendering is delegated to the shared
 * FormRenderer so the public page has full field-type parity with the
 * authenticated surfaces and applies the org's colors/fonts/radius. The legacy
 * `branding` triple is honored as a fallback for older payloads.
 */

import React, { useState, useEffect, useMemo } from 'react';
import { Loader2, CheckCircle, AlertCircle, Send, Languages, BarChart3, ShieldCheck } from 'lucide-react';
import { RawOption } from './fieldOptions';
import { cascadeVisibleValues, isFieldVisible, VisibilityRules } from './fieldVisibility';
import FormRenderer from './FormRenderer';
import CaptchaWidget from './CaptchaWidget';
import type { FormSectionSchema } from '@/services/api/forms';
import {
  mergeFormTheme,
  themeToCssVars,
  themeButtonStyle,
  themePageBackgroundStyle,
  type PartialFormTheme,
} from './formTheme';

import { config as productConfig } from '@/src/config';
import PhoneInput from '@/components/ui/PhoneInput';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { useDynamicTranslation } from './dynamicTranslation';
interface PublicFormPageProps {
  token: string;
}

interface FormSection {
  section_id: string;
  title: string;
  description?: string;
  fields: FormField[];
}

interface FormField {
  field_id: string;
  field_type: string;
  label: string;
  description?: string;
  required?: boolean;
  placeholder?: string;
  options?: RawOption[];
  validation?: Record<string, any>;
  // Carried from com_fields via the field-config sync; drives conditional
  // visibility on public intake forms (same evaluator as authenticated forms).
  data_source?: string;
  visibility_rules?: VisibilityRules | null;
  /** "Choose up to N" on a checkbox question (TRUS-624). */
  max_selections?: number;
}

interface FormDefinition {
  token: string;
  title: string;
  description?: string;
  sections: FormSection[];
  // Fully-resolved visual theme (design template -> org default -> org branding
  // -> defaults), assembled by the backend. Optional for back-compat.
  theme?: PartialFormTheme;
  branding: {
    logo_url?: string;
    primary_color?: string;
    organization_name?: string;
  };
  success_message: string;
  redirect_url?: string;
  /**
   * An anonymous survey link (TRUS-624): no name/email/phone block, nothing but
   * the questions is sent, and a hidden answer cannot keep a later question in
   * view. The server enforces all three regardless.
   */
  anonymous?: boolean;
  public_results_enabled?: boolean;
  results_url?: string | null;
  /** What the page must do to satisfy the server's spam gate. */
  spam?: {
    honeypot_field?: string;
    captcha_required?: boolean;
    captcha_provider?: string | null;
    captcha_site_key?: string | null;
  };
}

/**
 * Languages an anonymous visitor can pick between.
 *
 * An unauthenticated submitter has no user or member row to hold a language
 * preference, so the choice lives in the URL (`?lang=es`) and in component
 * state, and is sent with the submission as `submission_language`. That last
 * part is not a nicety: the notification-consent checkbox on VYC's referral
 * form is a legal record, and consent evidenced in a language the person does
 * not read is not consent.
 */
const FORM_LANGUAGES = [
  { code: 'en', label: 'English' },
  { code: 'es', label: 'Español' },
] as const;

/** The form's own "Form Language" question, kept in step with the toggle. */
const FORM_LANGUAGE_FIELD_ID = 'inbound_referral_form_language';
const FORM_LANGUAGE_VALUES: Record<string, string> = { en: 'English', es: 'Spanish' };

function readLangParam(): string | null {
  if (typeof window === 'undefined') return null;
  const raw = new URLSearchParams(window.location.search).get('lang');
  if (!raw) return null;
  const normalized = raw.trim().toLowerCase().split('-')[0];
  return FORM_LANGUAGES.some((l) => l.code === normalized) ? normalized : null;
}

const API_BASE = (typeof window !== 'undefined' ? (productConfig.weaveinApi.baseUrl ?? '') : '').trim().replace(/\/$/, '');

export default function PublicFormPage({ token }: PublicFormPageProps) {
  const { t, language, setLanguage } = useAppTranslation();
  // The language the visitor is actually reading, resolved once from ?lang= and
  // thereafter from the toggle. Kept locally as well as in the provider so the
  // value sent with the submission is exactly what was on screen.
  const [formLanguage, setFormLanguage] = useState<string>(() => readLangParam() || 'en');
  // Tenant-authored labels for the chosen language. Fetched separately from the
  // form itself so switching language re-fetches strings, not the whole form and
  // whatever the visitor has already typed into it.
  //
  // MUST stay declared ABOVE the useDynamicTranslation() call below: `const` is
  // hoisted into a temporal dead zone, so reading this binding before this line
  // throws `ReferenceError: Cannot access '<x>' before initialization` and the
  // whole page renders blank. Vite's dev transform tolerated the forward
  // reference; the minified production build did not, which took every public
  // form on weavein.ai down for four days (TRUS-418).
  const [dynamicTranslations, setDynamicTranslations] = useState<Record<string, string> | null>(null);
  const tr = useDynamicTranslation(dynamicTranslations);
  const [honeypot, setHoneypot] = useState('');
  const [form, setForm] = useState<FormDefinition | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [fieldData, setFieldData] = useState<Record<string, any>>({});
  const [submitting, setSubmitting] = useState(false);
  // Null until the submitter solves the challenge, and back to null when it
  // expires — a stale token is rejected by the provider, so it must not be sent.
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');
  const [submitName, setSubmitName] = useState('');
  const [submitEmail, setSubmitEmail] = useState('');
  const [submitPhone, setSubmitPhone] = useState('');

  useEffect(() => {
    const loadForm = async () => {
      try {
        const res = await fetch(`${API_BASE}/api/public/forms/${token}`);
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          setError(err.detail || 'Form not found or expired');
          return;
        }
        const data = await res.json();
        setForm(data);
      } catch {
        setError('Failed to load form');
      } finally {
        setLoading(false);
      }
    };
    loadForm();
  }, [token]);

  // Tenant-authored labels for this org, scoped by the form token — the token is
  // the authorization, since an anonymous visitor has no account. Best-effort:
  // if it fails the form still renders in the tenant's own language rather than
  // showing nothing, so a translation outage never blocks a referral.
  useEffect(() => {
    let cancelled = false;
    const loadTranslations = async () => {
      try {
        const res = await fetch(`${API_BASE}/api/public/forms/${token}/translations/${formLanguage}`);
        if (!res.ok) return;
        const data = await res.json();
        if (!cancelled) setDynamicTranslations(data?.translations || null);
      } catch {
        /* keep whatever we have; labels fall back to the tenant's own text */
      }
    };
    loadTranslations();
    return () => {
      cancelled = true;
    };
  }, [token, formLanguage]);

  // Deep link: /public/forms/<token>?lang=es. Applied once on mount so a link
  // shared in Spanish opens in Spanish, with no login and no stored preference.
  useEffect(() => {
    const requested = readLangParam();
    if (requested && requested !== language) {
      void setLanguage(requested);
    }
    // Intentionally mount-only: after this the toggle owns the language.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const chooseLanguage = async (code: string) => {
    setFormLanguage(code);
    // Keep the form's own "Form Language" answer in step with what is on screen,
    // where the template asks it, so the record and the page never disagree.
    const asksFormLanguage = (form?.sections || []).some((section) =>
      (section.fields || []).some((f) => f.field_id === FORM_LANGUAGE_FIELD_ID),
    );
    if (asksFormLanguage && FORM_LANGUAGE_VALUES[code]) {
      setFieldData((prev) => ({ ...prev, [FORM_LANGUAGE_FIELD_ID]: FORM_LANGUAGE_VALUES[code] }));
    }
    try {
      await setLanguage(code);
    } catch {
      /* the page still renders in the chosen language; only the string source failed */
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form) return;

    // Basic validation. A field hidden by its visibility rules cannot be
    // required — skip it so submitters aren't blocked by fields they can't see.
    for (const section of form.sections || []) {
      for (const field of section.fields || []) {
        if (!isFieldVisible(field, form.sections, answeredData)) continue;
        if (field.required && !answeredData[field.field_id]) {
          const label = tr.label(field, field.label);
          setError(t('public_form.field_required', `"${label}" is required`, { field: label }));
          return;
        }
        const chosen = answeredData[field.field_id];
        if (field.max_selections && Array.isArray(chosen) && chosen.length > field.max_selections) {
          const label = tr.label(field, field.label);
          setError(
            t('public_form.too_many_choices', `Please choose no more than ${field.max_selections} for "${label}"`, {
              field: label,
              count: field.max_selections,
            }),
          );
          return;
        }
      }
    }

    // The server rejects a missing token when a secret is configured, but its
    // error is generic. Catching it here names the actual problem.
    if (form.spam?.captcha_required && form.spam?.captcha_site_key && !captchaToken) {
      setError(
        t('public_form.captcha_required', 'Please complete the verification check below.'),
      );
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      const res = await fetch(`${API_BASE}/api/public/forms/${token}/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          field_data: form.anonymous
            ? // Anonymous: the questions and nothing else. No envelope fields,
              // and only answers the person can still see.
              { ...answeredData, [honeypotField]: honeypot }
            : {
                ...fieldData,
                submitted_name: submitName,
                submitted_email: submitEmail,
                submitted_phone: submitPhone,
                // Honeypot: invisible to a human, irresistible to a bot. Sent even
                // when empty so the server can tell "left blank" from "not rendered".
                [honeypotField]: honeypot,
              },
          submitted_by_name: form.anonymous ? undefined : submitName || undefined,
          submitted_by_email: form.anonymous ? undefined : submitEmail || undefined,
          submitted_by_phone: form.anonymous ? undefined : submitPhone || undefined,
          // Part of the consent audit trail — see FORM_LANGUAGES above.
          submission_language: formLanguage,
          captcha_token: captchaToken || undefined,
        }),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        setError(err.detail || 'Submission failed');
        // Challenge tokens are single-use. Keeping a spent one would make every
        // retry fail with the same opaque error.
        setCaptchaToken(null);
        return;
      }

      const result = await res.json();
      setSubmitted(true);
      setSubmitMessage(result.message || form.success_message);

      if (form.redirect_url) {
        setTimeout(() => { window.location.href = form.redirect_url!; }, 3000);
      }
    } catch {
      setError('Submission failed');
    } finally {
      setSubmitting(false);
    }
  };

  const updateField = (fieldId: string, value: any) => {
    setFieldData((prev) => ({ ...prev, [fieldId]: value }));
  };

  // What the person is actually answering. For an anonymous survey, an answer to
  // a question that skip logic has since hidden stops counting — the same
  // cascade the server applies — so changing "Yes" to "No" on a screening
  // question really does end the survey. What they typed is kept in fieldData,
  // so a section that comes back shows it again.
  const answeredData = useMemo(
    () => (form?.anonymous ? cascadeVisibleValues(form.sections, fieldData) : fieldData),
    [form, fieldData],
  );

  // Results links carry the language, so a Spanish respondent lands on Spanish.
  const resultsHref = form?.public_results_enabled
    ? `/public/forms/${token}/results${formLanguage === 'es' ? '?lang=es' : ''}`
    : null;

  // The honeypot's field name is per-token so a bot cannot learn one name and
  // skip it everywhere. Defaults to the server's own default for older payloads.
  const honeypotField = useMemo(() => form?.spam?.honeypot_field || '_hp_website', [form]);

  // Resolve the theme: prefer the backend-resolved `theme`; fall back to the
  // legacy branding triple (map primary_color -> colors.primary) so older
  // payloads still get their accent color.
  const rawTheme: PartialFormTheme | undefined =
    form?.theme ??
    (form?.branding?.primary_color ? { colors: { primary: form.branding.primary_color } } : undefined);
  const theme = mergeFormTheme(rawTheme);
  const primaryColor = theme.colors.primary;
  const logoUrl = theme.branding.logo_url || form?.branding?.logo_url || '';
  const orgName = form?.branding?.organization_name || '';
  const pageStyle = { ...themeToCssVars(rawTheme), ...themePageBackgroundStyle(rawTheme) };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
      </div>
    );
  }

  if (error && !form) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="p-6 bg-white rounded-xl shadow-sm max-w-md text-center">
          <AlertCircle className="w-10 h-10 text-red-400 mx-auto mb-3" />
          <h2 className="text-lg font-semibold text-gray-900 mb-1">{t('public_form.unavailable', 'Form Unavailable')}</h2>
          <p className="text-sm text-gray-500">{error}</p>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: theme.colors.background }}>
        <div className="p-8 bg-white rounded-xl shadow-sm max-w-md text-center">
          <CheckCircle className="w-12 h-12 mx-auto mb-4" style={{ color: primaryColor }} />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">{t('public_form.submitted', 'Submitted')}</h2>
          <p className="text-sm text-gray-600">{tr.publicForm('success_message', submitMessage)}</p>
          {resultsHref && (
            <a
              href={resultsHref}
              className="mt-5 inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg"
              style={themeButtonStyle(rawTheme)}
            >
              <BarChart3 className="w-4 h-4" aria-hidden="true" />
              {t('public_form.see_live_results', 'See live results')}
            </a>
          )}
          {form?.redirect_url && (
            <p className="text-xs text-gray-400 mt-3">{t('public_form.redirecting', 'Redirecting...')}</p>
          )}
        </div>
      </div>
    );
  }

  if (!form) return null;

  return (
    <div className="min-h-screen py-8 px-4" style={pageStyle}>
      <div className="mx-auto" style={{ maxWidth: theme.layout.max_width }}>
        {/* Header card: language toggle + branding. It sits on the theme's
            surface colour, like the form below it, because its text uses the
            theme's muted colour — readable on a card, not on an org's gradient
            page background (TRUS-624: LCC's purple theme made it illegible). */}
        <header
          className="mb-4 rounded-xl shadow-sm px-5 pt-4 pb-5"
          style={{ backgroundColor: theme.colors.surface, borderRadius: theme.controls.input_radius }}
        >
        {/* Language toggle. Shown to everyone, because the visitor is anonymous:
            there is no account to carry a preference, so the choice has to be on
            the page itself. */}
        <div className="mb-2 flex items-center justify-end gap-1">
          <Languages className="w-4 h-4 mr-1" style={{ color: theme.colors.muted }} aria-hidden="true" />
          <span className="sr-only">{t('public_form.language', 'Language')}</span>
          {FORM_LANGUAGES.map((option) => {
            const active = formLanguage === option.code;
            return (
              <button
                key={option.code}
                type="button"
                onClick={() => chooseLanguage(option.code)}
                aria-pressed={active}
                lang={option.code}
                className="px-3 py-1 text-xs font-medium rounded-full border transition-colors"
                style={{
                  borderColor: active ? primaryColor : theme.colors.border,
                  backgroundColor: active ? primaryColor : 'transparent',
                  color: active ? '#fff' : theme.colors.muted,
                }}
              >
                {option.label}
              </button>
            );
          })}
        </div>

        {/* Branding */}
        <div className="text-center">
          {theme.branding.show_logo && logoUrl && (
            <img src={logoUrl} alt="" className="h-12 mx-auto mb-3" />
          )}
          {orgName && (
            <p className="text-sm mb-1" style={{ color: theme.colors.muted }}>{orgName}</p>
          )}
          <h1 className="text-2xl font-bold" style={{ color: theme.colors.text, fontFamily: theme.typography.heading_font || theme.typography.font_family }}>
            {tr.publicForm('title', form.title)}
          </h1>
          {form.description && (
            <p className="text-sm mt-1" style={{ color: theme.colors.muted }}>
              {tr.publicForm('description', form.description)}
            </p>
          )}
          {(form.anonymous || resultsHref) && (
            <div className="mt-3 flex flex-wrap items-center justify-center gap-x-4 gap-y-1 text-xs">
              {form.anonymous && (
                <span className="inline-flex items-center gap-1" style={{ color: theme.colors.muted }}>
                  <ShieldCheck className="w-3.5 h-3.5" aria-hidden="true" />
                  {t('public_form.anonymous_notice', 'Anonymous — we do not ask for your name')}
                </span>
              )}
              {resultsHref && (
                <a href={resultsHref} className="inline-flex items-center gap-1 underline" style={{ color: primaryColor }}>
                  <BarChart3 className="w-3.5 h-3.5" aria-hidden="true" />
                  {t('public_form.see_live_results', 'See live results')}
                </a>
              )}
            </div>
          )}
        </div>
        </header>

        <form
          onSubmit={handleSubmit}
          className="rounded-xl shadow-sm p-6 space-y-6"
          style={{ backgroundColor: theme.colors.surface, borderRadius: theme.controls.input_radius }}
        >
          {/* Contact info — the submitter block. Not on an anonymous survey:
              asking for a name up front contradicts the promise above it, and
              the survey's own optional contact section does that job. */}
          {!form.anonymous && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pb-4 border-b" style={{ borderColor: theme.colors.border }}>
            <div>
              <label className="block text-xs font-medium mb-1" style={{ color: theme.colors.muted }}>
                {t('public_form.your_name', 'Your Name')}
              </label>
              <input type="text" value={submitName} onChange={(e) => setSubmitName(e.target.value)}
                className="w-full px-3 py-2 border text-sm"
                style={{ borderColor: theme.colors.border, borderRadius: theme.controls.input_radius, backgroundColor: theme.controls.input_bg, color: theme.colors.text }}
                placeholder={t('public_form.full_name', 'Full name')} />
            </div>
            <div>
              <label className="block text-xs font-medium mb-1" style={{ color: theme.colors.muted }}>
                {t('public_form.email', 'Email')}
              </label>
              <input type="email" value={submitEmail} onChange={(e) => setSubmitEmail(e.target.value)}
                className="w-full px-3 py-2 border text-sm"
                style={{ borderColor: theme.colors.border, borderRadius: theme.controls.input_radius, backgroundColor: theme.controls.input_bg, color: theme.colors.text }}
                placeholder="email@example.com" />
            </div>
            <div>
              <label className="block text-xs font-medium mb-1" style={{ color: theme.colors.muted }}>
                {t('public_form.phone', 'Phone')}
              </label>
              <PhoneInput value={submitPhone} onChange={setSubmitPhone}
                className="w-full px-3 py-2 border text-sm"
                placeholder="(xxx) xxx-xxxx" />
            </div>
          </div>
          )}

          {/* Form sections — delegated to the shared themable renderer, so the
              public page has full field-type parity with authenticated forms. */}
          <FormRenderer
            sections={form.sections as unknown as FormSectionSchema[]}
            value={answeredData}
            onChange={updateField}
            theme={rawTheme}
            dynamicTranslations={dynamicTranslations}
            // This page draws the logo in its own header above; without this
            // the renderer would draw a second one.
            brandHeader={false}
          />

          {/* Honeypot. Off-screen rather than display:none — some bots skip
              display:none inputs, and a screen reader is kept away by
              aria-hidden + tabIndex -1 rather than by hiding the field from the
              accessibility tree only. A human never sees or focuses this. */}
          <div aria-hidden="true" style={{ position: 'absolute', left: '-9999px', height: 0, overflow: 'hidden' }}>
            <label htmlFor={honeypotField}>Leave this field empty</label>
            <input
              id={honeypotField}
              name={honeypotField}
              type="text"
              tabIndex={-1}
              autoComplete="off"
              value={honeypot}
              onChange={(e) => setHoneypot(e.target.value)}
            />
          </div>

          {/* Verification challenge. Rendered only when the token asks for one
              AND the server supplied a site key — without the key the provider
              cannot render, and blocking submission on a widget that will never
              appear would take the form down. */}
          {form.spam?.captcha_required && form.spam?.captcha_site_key && (
            <CaptchaWidget
              provider={form.spam.captcha_provider}
              siteKey={form.spam.captcha_site_key}
              language={formLanguage}
              onToken={setCaptchaToken}
            />
          )}

          {error && (
            <div className="p-3 text-sm rounded-lg" style={{ backgroundColor: '#FEF2F2', color: theme.colors.error }}>{error}</div>
          )}

          <button
            type="submit"
            disabled={submitting}
            className="w-full flex items-center justify-center gap-2 py-3 text-sm font-medium rounded-lg disabled:opacity-50"
            style={themeButtonStyle(rawTheme)}
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            {submitting ? t('public_form.submitting', 'Submitting...') : t('public_form.submit', 'Submit')}
          </button>
        </form>

        {theme.branding.show_powered_by && (
          <p className="text-xs text-center mt-4" style={{ color: theme.colors.muted }}>
            Powered by WeaveIn.ai
          </p>
        )}
      </div>
    </div>
  );
}
